import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Package, Users, ShoppingCart, IndianRupee, Truck, CheckCircle2, RotateCcw,
  AlertTriangle, Boxes, TrendingUp, Wallet,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import api, { fmtINR } from "@/lib/api";
import { StatCard, PageHeader } from "@/components/common";

const CHANNEL_COLORS = { store: "#FF5722", amazon: "#F59E0B", flipkart: "#3B82F6", manual: "#94A3B8" };

export default function AdminDashboard() {
  const { data: d } = useQuery({ queryKey: ["admin-dash"], queryFn: async () => (await api.get("/admin/dashboard")).data });
  const { data: sales } = useQuery({ queryKey: ["sales-by-day"], queryFn: async () => (await api.get("/admin/reports/sales-by-day")).data });
  const { data: channels } = useQuery({ queryKey: ["orders-by-channel"], queryFn: async () => (await api.get("/admin/reports/orders-by-channel")).data });
  const { data: topProducts } = useQuery({ queryKey: ["top-products"], queryFn: async () => (await api.get("/admin/reports/top-products")).data });
  const { data: agentPerf } = useQuery({ queryKey: ["agent-perf"], queryFn: async () => (await api.get("/admin/reports/agent-performance")).data });

  if (!d) return <p className="text-sm text-slate-500" data-testid="dashboard-loading">Loading dashboard…</p>;

  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Control Center" sub="Complete operational visibility across products, agents, orders and profit." testid="admin-dashboard-title" />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <StatCard testid="stat-total-products" icon={Package} label="Products" value={d.total_products} sub={`${d.active_products} active`} />
        <StatCard testid="stat-total-agents" icon={Users} label="Agents" value={d.total_agents} sub={`${d.active_agents} active`} accent="text-blue-400" />
        <StatCard testid="stat-total-orders" icon={ShoppingCart} label="Orders" value={d.total_orders} sub={`${d.today_orders} today`} accent="text-cyan-400" />
        <StatCard testid="stat-month-sales" icon={IndianRupee} label="Monthly Sales" value={fmtINR(d.month_sales)} sub={`Today ${fmtINR(d.today_sales)}`} />
        <StatCard testid="stat-pending-shipments" icon={Truck} label="Pending Shipments" value={d.pending_shipments} sub={`${d.delivered} delivered`} accent="text-amber-400" />
        <StatCard testid="stat-profit" icon={TrendingUp} label="Profit (Delivered)" value={fmtINR(d.profit)} sub={`Est. ${fmtINR(d.estimated_profit)}`} accent="text-emerald-400" />
        <StatCard testid="stat-returns" icon={RotateCcw} label="Returns" value={d.returns} sub={`${d.rto} RTO · ${d.ndr} NDR`} accent="text-rose-400" />
        <StatCard testid="stat-low-stock" icon={AlertTriangle} label="Low Stock" value={d.low_stock} sub="At/below reorder level" accent="text-amber-400" />
        <StatCard testid="stat-outstanding" icon={Wallet} label="Agent Outstanding" value={fmtINR(d.agent_outstanding)} accent="text-rose-400" />
        <StatCard testid="stat-admin-revenue" icon={IndianRupee} label="Admin Revenue" value={fmtINR(d.admin_revenue)} sub="Product cost billed" accent="text-violet-400" />
        <StatCard testid="stat-inventory" icon={Boxes} label="Delivered Orders" value={d.delivered} sub={`${d.month_orders} this month`} accent="text-blue-400" />
        <StatCard testid="stat-total-sales" icon={TrendingUp} label="Total Sales" value={fmtINR(d.total_sales)} sub="All time" accent="text-emerald-400" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5 lg:col-span-2" data-testid="chart-daily-sales">
          <h3 className="mb-4 text-sm font-semibold text-slate-200">Daily Sales (14 days)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={sales || []}>
              <defs>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF5722" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#FF5722" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fill: "#64748B", fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 }} />
              <Area type="monotone" dataKey="sales" stroke="#FF5722" fill="url(#salesGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="chart-orders-channel">
          <h3 className="mb-4 text-sm font-semibold text-slate-200">Orders by Channel</h3>
          {channels?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={channels} dataKey="orders" nameKey="channel" innerRadius={55} outerRadius={80} paddingAngle={3}>
                  {channels.map((c) => <Cell key={c.channel} fill={CHANNEL_COLORS[c.channel] || "#94A3B8"} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="py-16 text-center text-xs text-slate-500">No orders yet</p>}
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="top-products-table">
          <h3 className="mb-3 text-sm font-semibold text-slate-200">Top Products</h3>
          <div className="space-y-2">
            {(topProducts || []).slice(0, 6).map((p, i) => (
              <div key={p.sku} className="flex items-center justify-between rounded-lg bg-slate-900/60 px-3 py-2">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-orange-400">#{i + 1}</span>
                  <div>
                    <p className="text-xs font-medium text-slate-200">{p.name}</p>
                    <p className="font-mono text-[10px] text-slate-500">{p.sku}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs font-semibold text-slate-100">{fmtINR(p.revenue)}</p>
                  <p className="text-[10px] text-slate-500">{p.qty} sold</p>
                </div>
              </div>
            ))}
            {!topProducts?.length && <p className="py-8 text-center text-xs text-slate-500">No sales data yet</p>}
          </div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="agent-performance-table">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-200">Agent Performance</h3>
            <Link to="/admin/agents" className="text-xs text-orange-400 hover:text-orange-300" data-testid="view-agents-link">View all</Link>
          </div>
          <div className="space-y-2">
            {(agentPerf || []).map((a) => (
              <Link to={`/admin/agents/${a.agent_id}`} key={a.agent_id}
                className="flex items-center justify-between rounded-lg bg-slate-900/60 px-3 py-2 transition-colors hover:bg-slate-800">
                <div>
                  <p className="text-xs font-medium text-slate-200">{a.business_name}</p>
                  <p className="text-[10px] text-slate-500">{a.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-semibold text-slate-100">{fmtINR(a.sales)}</p>
                  <p className="text-[10px] text-slate-500">{a.orders} orders · {fmtINR(a.profit)} profit</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
